// What an action card actually says, for the three surfaces that render one.
//
// DEF-11: a card carries three things the advisor needs and the dashboard
// showed none of them - the reason it was raised, the sentence the server
// wrote explaining it (`meta.details`), and the name of the file it is about.
// Every `needs_user_input:*` reason the live client raises in practice
// (attachment_not_published, attachment_needs_manual_review,
// multi_property_attachment, unverified_reply, manual_reply_failed …) is absent
// from the per-surface label maps, so all of them collapsed onto a bare
// "Input Needed" chip and a generic "Your Input Needed" panel. The advisor was
// told to review a file that was never named.
//
// This map deliberately holds ONLY the subreasons that had no label anywhere.
// confidential / client_question / negotiation / legal_contract / scheduling /
// unclear already have per-surface copy that differs on purpose ("Client
// Question" on the chip, "Question About Requirements" in the sidebar), and
// rewriting it is not this fix's business - each surface still checks its own
// map first and falls through to here.

export const NEEDS_USER_INPUT_PREFIX = 'needs_user_input:';

export const NEEDS_USER_INPUT_REASON_LABELS = {
  an_inbound_message_matched_no_conversation: 'Unmatched Message',
  duplicate_row_skipped: 'Duplicate Row Skipped',
  contact_opted_out_not_sent: 'Contact Opted Out — Not Sent',
  reply_after_stop: 'Reply After Stop',
  an_inbound_message_this_turn_did_nothing_with: 'Message Not Acted On',
  attachment_could_not_be_read: 'Attachment Could Not Be Read',
  attachment_needs_manual_review: 'Attachment Needs Your Review',
  attachment_not_published: 'Attachment Not Saved',
  email_delayed: 'Email Delayed',
  email_not_delivered: 'Email Not Delivered',
  manual_reply_failed: 'Reply Not Delivered',
  missing_size: 'Size Not Given',
  multi_property_attachment: 'Several Properties In One Attachment',
  new_property: 'New Property Mentioned',
  reply_withheld_broker_promised_fields: 'Reply Held',
  tour_requested: 'Tour Requested',
  unverified_reply: 'Unverified Reply'
};

export function getNeedsUserInputSubreason(reason) {
  const value = String(reason || '').trim();
  if (!value.startsWith(NEEDS_USER_INPUT_PREFIX)) return null;
  const subreason = value.slice(NEEDS_USER_INPUT_PREFIX.length).trim();
  return subreason || null;
}

export function getNeedsUserInputLabel(reason) {
  const subreason = getNeedsUserInputSubreason(reason);
  if (!subreason) return null;
  return NEEDS_USER_INPUT_REASON_LABELS[subreason] || null;
}

// The server's own explanation of the card, shown verbatim. It is written for
// the advisor ("A broker file was read in full, but it could not be stored
// where you can open it …"), so it is not paraphrased or truncated here.
export function getActionCardDetails(notification) {
  const details = notification?.meta?.details ?? notification?.details;
  const text = String(details || '').trim();
  return text || null;
}

function assetName(asset) {
  if (typeof asset === 'string') return asset.trim();
  return String(asset?.name || asset?.fileName || asset?.filename || '').trim();
}

// Only a real http(s) location may become a link. A card whose file could not
// be published carries no usable location, and a `javascript:`/`data:` value
// must never reach an href.
function assetUrl(asset) {
  if (typeof asset === 'string') return null;
  const raw = String(
    asset?.driveLink || asset?.webUrl || asset?.url || asset?.link || ''
  ).trim();
  if (!raw) return null;
  return /^https?:\/\//i.test(raw) ? raw : null;
}

function toList(value) {
  if (Array.isArray(value)) return value;
  if (value === undefined || value === null || value === '') return [];
  return [value];
}

// The file(s) a card is about, named. `meta.assets` is what the live cards
// carry; the plain-name fields are accepted so a card that only lists names
// still names something. `meta.hasAttachments` is a boolean, not a name, and
// deliberately produces nothing - a placeholder that looks like a filename
// would be worse than silence.
export function getActionCardAttachments(notification) {
  const meta = notification?.meta || {};
  const candidates = [
    ...toList(meta.assets),
    ...toList(notification?.assets),
    ...toList(meta.attachmentNames),
    ...toList(notification?.attachmentNames),
    ...toList(meta.attachmentName),
    ...toList(meta.fileName)
  ];

  const seen = new Set();
  const attachments = [];
  candidates.forEach(candidate => {
    const name = assetName(candidate);
    if (!name || seen.has(name)) return;
    seen.add(name);
    attachments.push({ name, url: assetUrl(candidate) });
  });

  return attachments;
}

// Backend rowNumber already identifies the one-based sheet row, including the header.
export function getActionCardRowLabel(notification) {
  const row = notification?.rowNumber ?? notification?.meta?.rowNumber;
  return row != null && Number.isFinite(Number(row)) ? `Row ${Number(row)}` : '';
}

export function getActionCardPropertyLabel(notification) {
  const meta = notification?.meta || {};
  return notification?.rowAnchor || meta.rowAnchor || meta.propertyAddress ||
    meta.address || meta.property?.address || meta.property?.name ||
    meta.propertyName || getActionCardRowLabel(notification);
}
